import React, { useState, useEffect } from 'react';
import Layout from '../components/common/Layout';
import KanbanBoard from '../components/kanban/KanbanBoard';
import StepSidebar from '../components/template/StepSidebar';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import { apiService } from '../services/api';
import { notifications } from '../utils/notifications';
import type { Step, Question, Template } from '../types';

const EditTemplate: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [template, setTemplate] = useState<Template | null>(null);
  const [templateName, setTemplateName] = useState('');
  const [originalTemplateName, setOriginalTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [editingName, setEditingName] = useState(false);
  const [editingDescription, setEditingDescription] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalStep, setModalStep] = useState<number | null>(null);
  const [modalQuestion, setModalQuestion] = useState('');
  const [modalRequired, setModalRequired] = useState<'yes' | 'no'>('no');
  const [modalInsertPosition, setModalInsertPosition] = useState<number | null>(null);

  // Edit Question modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editCurrentQuestion, setEditCurrentQuestion] = useState('');
  const [editNewQuestion, setEditNewQuestion] = useState('');
  const [editRequired, setEditRequired] = useState<'yes' | 'no'>('no');
  const [editQuestionId, setEditQuestionId] = useState<number | null>(null);

  // Question operation states
  const [questionOperationLoading, setQuestionOperationLoading] = useState(false);
  const [deletingQuestionId, setDeletingQuestionId] = useState<number | null>(null);

  // Step navigation state
  const [activeStepId, setActiveStepId] = useState<number>(1);

  // New header state
  const [autoSavedVisible, setAutoSavedVisible] = useState(false);
  const [currentMode, setCurrentMode] = useState<'prep' | 'present'>(() => {
    const savedMode = localStorage.getItem('editTemplateMode');
    return (savedMode === 'present' || savedMode === 'prep') ? savedMode : 'prep';
  });
  const [isTransitioning, setIsTransitioning] = useState(false);

  // API state
  const [steps, setSteps] = useState<Step[]>([]);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Handle saving template changes
  const handleSaveChanges = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!template) {
      toast.error('No worksheet data available to save');
      return;
    }

    try {
      setSaving(true);

      const templateId = template.id;
      const updateData = {
        name: templateName,
        description: templateDescription,
      };

      const updatedTemplate = await apiService.updateTemplate(templateId, updateData);
      setTemplate(updatedTemplate);
      toast.success('Worksheet saved successfully! 🎉');
    } catch (err) {
      console.error('Failed to save template:', err);
      toast.error('Failed to save worksheet. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Helper function to refresh questions after CRUD operations
  const refreshQuestions = async () => {
    if (!template) return;
    
    try {
      const updatedTemplate = await apiService.getTemplate(template.id);
      setQuestions(updatedTemplate.questions);
      setTemplate(updatedTemplate);
    } catch (err) {
      console.error('Failed to refresh questions:', err);
      toast.error('Failed to refresh questions');
    }
  };

  // Handle adding a new question
  const handleAddQuestion = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!template || modalStep === null) {
      toast.error('Invalid worksheet or step data');
      return;
    }

    if (!modalQuestion.trim()) {
      toast.error('Please enter a question');
      return;
    }

    try {
      setQuestionOperationLoading(true);

      // Get the step number from the selected step
      const selectedStep = steps[modalStep];
      if (!selectedStep) {
        toast.error('Invalid step selected');
        return;
      }

      // Calculate question order based on insert position
      const stepQuestions = questions.filter(q => q.step_number === selectedStep.step_number).sort((a, b) => a.question_order - b.question_order);
      let questionOrder: number;
      
      if (modalInsertPosition !== null) {
        // Insert at specific position (modalInsertPosition is the question_order we want to insert at)
        questionOrder = modalInsertPosition;
        
        // Update order of existing questions that come at or after the insert position
        // We need to shift all questions at or after this position by +1
        const questionsToUpdate = stepQuestions.filter(q => q.question_order >= questionOrder);
        
        // Wait for all reordering to complete before creating the new question
        await Promise.all(
          questionsToUpdate.map(async (q) => {
            return apiService.updateQuestion(q.id, {
              question_order: q.question_order + 1
            });
          })
        );
        
        // Small delay to ensure backend processes all updates
        await new Promise(resolve => setTimeout(resolve, 100));
      } else {
        // Add at end (existing behavior) - use next available order for this step
        const maxOrder = stepQuestions.length > 0 ? Math.max(...stepQuestions.map(q => q.question_order)) : 0;
        questionOrder = maxOrder + 1;
      }

      const newQuestionData = {
        template_id: template.id,
        step_number: selectedStep.step_number,
        question_text: modalQuestion.trim(),
        question_order: questionOrder,
        is_required: modalRequired === 'yes',
      };

      await apiService.createQuestion(newQuestionData);
      await refreshQuestions();
      
      // Reset modal state
      setShowModal(false);
      setModalQuestion('');
      setModalRequired('no');
      setModalStep(null);
      setModalInsertPosition(null);
      
      toast.success('Question added successfully! ✨');
    } catch (err) {
      console.error('Failed to add question:', err);
      toast.error('Failed to add question. Please try again.');
    } finally {
      setQuestionOperationLoading(false);
    }
  };

  // Handle updating an existing question
  const handleUpdateQuestion = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!editQuestionId) {
      toast.error('No question selected for editing');
      return;
    }

    if (!editNewQuestion.trim()) {
      toast.error('Please enter a question');
      return;
    }

    try {
      setQuestionOperationLoading(true);

      const updateData = {
        question_text: editNewQuestion.trim(),
        is_required: editRequired === 'yes',
      };

      await apiService.updateQuestion(editQuestionId, updateData);
      await refreshQuestions();
      
      // Reset modal state
      setShowEditModal(false);
      setEditQuestionId(null);
      setEditCurrentQuestion('');
      setEditNewQuestion('');
      setEditRequired('no');
      
      toast.success('Question updated successfully! 📝');
    } catch (err) {
      console.error('Failed to update question:', err);
      toast.error('Failed to update question. Please try again.');
    } finally {
      setQuestionOperationLoading(false);
    }
  };

  // Handle deleting a question
  const handleDeleteQuestion = async () => {
    if (!editQuestionId) {
      toast.error('No question selected for deletion');
      return;
    }

    try {
      setDeletingQuestionId(editQuestionId);

      await apiService.deleteQuestion(editQuestionId);
      await refreshQuestions();
      
      // Reset modal state
      setShowEditModal(false);
      setEditQuestionId(null);
      setEditCurrentQuestion('');
      setEditNewQuestion('');
      setEditRequired('no');
      
      notifications.showSuccess('Deleted!', 'Question has been deleted successfully.');
    } catch (err) {
      console.error('Failed to delete question:', err);
      toast.error('Failed to delete question. Please try again.');
    } finally {
      setDeletingQuestionId(null);
    }
  };

  // Handle direct deletion from QuestionCard
  const handleDeleteQuestionFromKanban = async (question: Question) => {
    const result = await Swal.fire({
      title: 'Delete Question',
      text: `Are you sure you want to delete this question: "${question.question_text}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      try {
        setDeletingQuestionId(question.id);

        await apiService.deleteQuestion(question.id);
        await refreshQuestions();
        
        notifications.showSuccess('Deleted!', 'Question has been deleted successfully.');
      } catch (err) {
        console.error('Failed to delete question:', err);
        toast.error('Failed to delete question. Please try again.');
      } finally {
        setDeletingQuestionId(null);
      }
    }
  };

  const handlePreviewTemplate = () => {
    notifications.comingSoon('Preview functionality');
  }

  // Handle mode change with localStorage persistence
  const handleModeChange = (mode: 'prep' | 'present') => {
    setIsTransitioning(true);
    
    // Small delay to allow fade out animation
    setTimeout(() => {
      setCurrentMode(mode);
      localStorage.setItem('editTemplateMode', mode);
      
      // Small delay to allow the DOM to update before fade in
      setTimeout(() => {
        setIsTransitioning(false);
      }, 50);
    }, 150);
  };

  // Handle reordering a question
  const handleReorderQuestion = async (questionId: number, newStepNumber: number, newOrder: number): Promise<void> => {
    console.log('Reorder API called:', { questionId, newStepNumber, newOrder });
    try {
      await apiService.reorderQuestion(questionId, newStepNumber, newOrder);
      toast.success('Question moved successfully! 🎯');
    } catch (err) {
      console.error('Failed to reorder question:', err);
      toast.error('Failed to move question. Please try again.');
      throw err; // Re-throw to allow the KanbanBoard to handle the error
    }
  };

  // Handle questions update from KanbanBoard
  const handleQuestionsUpdate = (updatedQuestions: Question[]) => {
    setQuestions(updatedQuestions);
  };

  // Handle step selection from sidebar
  const handleStepSelect = (stepNumber: number) => {
    setActiveStepId(stepNumber);
  };

  // Handle editing a question from KanbanBoard
  const handleEditQuestionFromKanban = (question: Question) => {
    setShowEditModal(true);
    setEditQuestionId(question.id);
    setEditCurrentQuestion(question.question_text);
    setEditNewQuestion(question.question_text);
    setEditRequired(question.is_required ? 'yes' : 'no');
  };

  // Handle adding a question from KanbanBoard
  const handleAddQuestionFromKanban = (stepNumber: number) => {
    const stepIndex = steps.findIndex(s => s.step_number === stepNumber);
    if (stepIndex !== -1) {
      setShowModal(true);
      setModalStep(stepIndex);
      setModalQuestion('');
      setModalRequired('no');
    }
  };

  // Handle inserting a question at specific position
  const handleInsertQuestion = (stepNumber: number, insertAtOrder: number) => {
    const stepIndex = steps.findIndex(s => s.step_number === stepNumber);
    if (stepIndex !== -1) {
      setShowModal(true);
      setModalStep(stepIndex);
      setModalQuestion('');
      setModalRequired('no');
      setModalInsertPosition(insertAtOrder);
    }
  };

  // Fallback steps for when API is not available
  const fallbackSteps: Step[] = [
    { id: 1, step_number: 1, step_name: 'Awareness', step_description: null },
    { id: 2, step_number: 2, step_name: 'Understanding', step_description: null },
    { id: 3, step_number: 3, step_name: 'Expectations', step_description: null },
    { id: 4, step_number: 4, step_name: 'Systems', step_description: null },
    { id: 5, step_number: 5, step_name: 'Follow-Up', step_description: null }
  ];

  // Fetch template data from API
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Get template ID from route params, fallback to 1 if not provided
        const templateId = id ? parseInt(id, 10) : 1;
        if (isNaN(templateId)) {
          throw new Error('Invalid template ID');
        }
        
        // Fetch template with questions
        const fetchedTemplate = await apiService.getTemplate(templateId);
        setTemplate(fetchedTemplate);
        setTemplateName(fetchedTemplate.name);
        setOriginalTemplateName(fetchedTemplate.name);
        setTemplateDescription(fetchedTemplate.description);
        setQuestions(fetchedTemplate.questions);
        
        // Fetch steps separately
        const fetchedSteps = await apiService.getSteps();
        setSteps(fetchedSteps);
        
        setError(null);
      } catch (err) {
        console.warn('Failed to fetch data from API, using fallback:', err);
        setSteps(fallbackSteps);
        setQuestions([]); // Empty questions for fallback
        setTemplateName('Manager Skills Development');
        setOriginalTemplateName('Manager Skills Development');
        setTemplateDescription('Self & Personnel Management');
        setError('Using offline data - API not available');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  // Show loading state
  if (loading) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-2 py-8">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading worksheet...</p>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-2 py-8">
        {/* New Merged Header Card */}
        <div className="bg-white rounded-lg shadow mb-8" style={{ height: '65px' }}>
          <div className="flex items-center justify-between h-full px-6">
            {/* Left Side - Breadcrumb with editable template name */}
            <div className="flex items-center space-x-2">
              {/* Back arrow and Dashboard link */}
              <button
                onClick={() => navigate('/dashboard')}
                className="text-blue-400 hover:text-blue-600 hover:underline font-medium text-sm flex items-center space-x-1 transition-all duration-200"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                <span>Dashboard</span>
              </button>
              
              <span className="text-gray-400 text-sm">/</span>
              
              {/* Editable Template Name */}
              {editingName ? (
                <input
                  type="text"
                  value={templateName}
                  autoFocus
                  onBlur={async () => {
                    // Check if template name is empty or just whitespace
                    if (!templateName.trim()) {
                      toast.error('Worksheet name cannot be empty');
                      setTemplateName(originalTemplateName);
                      setEditingName(false);
                      return;
                    }

                    setEditingName(false);
                    if (template && templateName !== originalTemplateName) {
                      try {
                        setSaving(true);
                        const updatedTemplate = await apiService.updateTemplate(template.id, { name: templateName.trim() });
                        setTemplate(updatedTemplate);
                        setOriginalTemplateName(templateName.trim());
                        setAutoSavedVisible(true);
                        setTimeout(() => setAutoSavedVisible(false), 3000);
                        toast.success('Worksheet name updated! 📝');
                      } catch (err) {
                        console.error('Failed to save template name:', err);
                        toast.error('Failed to update worksheet name');
                        setTemplateName(originalTemplateName); // Revert on error
                      } finally {
                        setSaving(false);
                      }
                    }
                  }}
                  onKeyDown={async (e) => {
                    if (e.key === 'Enter') {
                      // Check if template name is empty or just whitespace
                      if (!templateName.trim()) {
                        toast.error('Worksheet name cannot be empty');
                        setTemplateName(originalTemplateName);
                        setEditingName(false);
                        return;
                      }

                      setEditingName(false);
                      if (template && templateName !== originalTemplateName) {
                        try {
                          setSaving(true);
                          const updatedTemplate = await apiService.updateTemplate(template.id, { name: templateName.trim() });
                          setTemplate(updatedTemplate);
                          setOriginalTemplateName(templateName.trim());
                          setAutoSavedVisible(true);
                          setTimeout(() => setAutoSavedVisible(false), 3000);
                          toast.success('Worksheet name updated! 📝');
                        } catch (err) {
                          console.error('Failed to save template name:', err);
                          toast.error('Failed to update worksheet name');
                          setTemplateName(originalTemplateName); // Revert on error
                        } finally {
                          setSaving(false);
                        }
                      }
                    } else if (e.key === 'Escape') {
                      setTemplateName(originalTemplateName);
                      setEditingName(false);
                    }
                  }}
                  onChange={(e) => setTemplateName(e.target.value)}
                  className="text-lg font-bold text-gray-900 bg-transparent border border-primary-500 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  style={{ minWidth: '400px' }}
                />
              ) : (
                <div className="relative group">
                  <h1
                    className="text-lg font-bold text-gray-900 cursor-pointer hover:text-primary-600 hover:underline transition-all duration-200"
                    onClick={() => {
                      setOriginalTemplateName(templateName);
                      setEditingName(true);
                    }}
                  >
                    {templateName}
                  </h1>
                  <div className="absolute -bottom-6 left-0 bg-gray-800 text-white text-xs rounded px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10">
                    Click to edit
                  </div>
                </div>
              )}
            </div>

            {/* Right Side - Auto-saved indicator and mode toggle */}
            <div className="flex items-center space-x-4">
              {/* Auto-saved indicator */}
              <div className={`flex items-center space-x-1 text-sm transition-colors duration-200 ${
                autoSavedVisible ? 'text-green-600' : 'text-gray-400'
              }`}>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span>Auto-saved</span>
              </div>

              {/* Mode Toggle Buttons */}
              <div className="flex bg-gray-100 rounded-lg p-1">
                <button
                  className={`px-4 py-1 rounded-md text-sm font-medium transition-colors duration-200 flex items-center space-x-2 ${
                    currentMode === 'prep' 
                      ? 'bg-secondary-500 text-white shadow-sm' 
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                  onClick={() => handleModeChange('prep')}
                >
                  {/* Horizontal lines icon for Prep Mode */}
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                  <span>Prep Mode</span>
                </button>
                <button
                  className={`px-4 py-1 rounded-md text-sm font-medium transition-colors duration-200 flex items-center space-x-2 ${
                    currentMode === 'present' 
                      ? 'bg-secondary-500 text-white shadow-sm' 
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                  onClick={() => handleModeChange('present')}
                >
                  {/* Vertical columns icon for Present Mode */}
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-4 h-4" >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2"></path>
                  </svg>
                  <span>Table Mode</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Area with Fade Transition */}
        <div className={`flex-1 flex transition-opacity duration-200 ${isTransitioning ? 'opacity-0' : 'opacity-100'}`}>
          {currentMode === 'prep' ? (
            <>
              {/* Prep Mode: Sidebar + KanbanBoard Layout */}
              <StepSidebar
                steps={steps}
                questions={questions}
                activeStepId={activeStepId}
                onStepSelect={handleStepSelect}
              />

              {/* Main Content */}
              <div className="flex-1 flex flex-col">
                <div className="flex-1 flex flex-col">
                  {/* Kanban Board */}
                  <KanbanBoard
                    steps={steps}
                    questions={questions}
                    activeStepId={activeStepId}
                    onQuestionsUpdate={handleQuestionsUpdate}
                    onEditQuestion={handleEditQuestionFromKanban}
                    onDeleteQuestion={handleDeleteQuestionFromKanban}
                    onAddQuestion={handleAddQuestionFromKanban}
                    onInsertQuestion={handleInsertQuestion}
                    onReorderQuestion={handleReorderQuestion}
                    deletingQuestionId={deletingQuestionId}
                    isOperationLoading={questionOperationLoading}
                    isPresentMode={false}
                  />
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Present Mode: Full Width KanbanBoard */}
              <div className="flex-1 flex flex-col">
                <div className="flex-1 flex flex-col">
                  {/* Full Width Kanban Board for Present Mode */}
                  <KanbanBoard
                    steps={steps}
                    questions={questions}
                    activeStepId={null} // Show all steps in present mode
                    onQuestionsUpdate={handleQuestionsUpdate}
                    onEditQuestion={handleEditQuestionFromKanban}
                    onDeleteQuestion={handleDeleteQuestionFromKanban}
                    onAddQuestion={handleAddQuestionFromKanban}
                    onInsertQuestion={handleInsertQuestion}
                    onReorderQuestion={handleReorderQuestion}
                    deletingQuestionId={deletingQuestionId}
                    isOperationLoading={questionOperationLoading}
                    isPresentMode={true} // Add flag to indicate present mode
                  />
                </div>
              </div>
            </>
          )}
        </div>
        
        {/* Add Question Modal (fade transition) */}
        <div
          className={`fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 transition-opacity duration-300 ${showModal ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
          aria-hidden={!showModal}
        >
          {showModal && modalStep !== null && (
            <div className="bg-white rounded-lg shadow-lg w-full max-w-lg p-6 relative transform transition-all duration-300 scale-100 opacity-100">
              <div className="pb-4 mb-6 border-b border-gray-200">
                <h2 className="text-2xl font-bold text-gray-900">
                  {modalInsertPosition !== null ? 
                    `Insert Question in Step ${modalStep + 1}: ${steps[modalStep].step_name}` :
                    `Add Question to Step ${modalStep + 1}: ${steps[modalStep].step_name}`
                  }
                </h2>
              </div>
              <form>
                <div className="mb-4">
                  <label htmlFor="modalQuestion" className="block text-sm font-medium text-gray-700 mb-2">
                    Type your question:
                  </label>
                  <textarea
                    id="modalQuestion"
                    rows={3}
                    value={modalQuestion}
                    onChange={e => setModalQuestion(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div className="mb-6">
                  <span className="block text-sm font-medium text-gray-700 mb-2">Is this field required?</span>
                  <div className="flex space-x-6">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="modalRequired"
                        value="yes"
                        checked={modalRequired === 'yes'}
                        onChange={() => setModalRequired('yes')}
                        className="mr-2"
                      />
                      Yes
                    </label>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="modalRequired"
                        value="no"
                        checked={modalRequired === 'no'}
                        onChange={() => setModalRequired('no')}
                        className="mr-2"
                      />
                      No
                    </label>
                  </div>
                </div>
                <div className="flex justify-end space-x-3">
                    <button
                      type="button"
                      className="px-4 py-2 bg-gray-200 rounded-md text-gray-700 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={() => setShowModal(false)}
                      disabled={questionOperationLoading}
                    >
                      Cancel
                    </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-primary-600 rounded-md text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={handleAddQuestion}
                    disabled={questionOperationLoading}
                  >
                    {questionOperationLoading ? 'Adding...' : 'Add Question'}
                  </button>
                </div>
              </form>
              <button
                className="absolute top-2 right-2 text-gray-400 hover:text-gray-600"
                onClick={() => setShowModal(false)}
                aria-label="Close"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-6 h-6">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          )}
        </div>

        {/* Edit Question Modal (fade transition) */}
        <div
          className={`fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 transition-opacity duration-300 ${showEditModal ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
          aria-hidden={!showEditModal}
        >
          {showEditModal && editQuestionId !== null && (
            <div className="bg-white rounded-lg shadow-lg w-full max-w-lg p-6 relative transform transition-all duration-300 scale-100 opacity-100">
              <div className="pb-4 mb-6 border-b border-gray-200">
                <h2 className="text-2xl font-bold text-gray-900">
                  Edit Question
                </h2>
              </div>
              <form>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Current Question</label>
                  <textarea
                    value={editCurrentQuestion}
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-500 resize-none"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Change to:</label>
                  <textarea
                    value={editNewQuestion}
                    onChange={e => setEditNewQuestion(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div className="mb-6">
                  <span className="block text-sm font-medium text-gray-700 mb-2">Is this field required?</span>
                  <div className="flex space-x-6">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="editRequired"
                        value="yes"
                        checked={editRequired === 'yes'}
                        onChange={() => setEditRequired('yes')}
                        className="mr-2"
                      />
                      Yes
                    </label>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="editRequired"
                        value="no"
                        checked={editRequired === 'no'}
                        onChange={() => setEditRequired('no')}
                        className="mr-2"
                      />
                      No
                    </label>
                  </div>
                </div>
                <div className="flex justify-between space-x-3">
                  <button
                    type="button"
                    className="px-4 py-2 bg-red-100 text-red-700 rounded-md hover:bg-red-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={async () => {
                      if (editQuestionId) {
                        const confirmed = await notifications.confirm({
                          title: 'Delete Question',
                          message: 'Are you sure you want to delete this question? This action cannot be undone.',
                          confirmText: 'Yes, delete it',
                          cancelText: 'Cancel',
                          type: 'warning',
                          dangerMode: true
                        });
                        if (confirmed) {
                          handleDeleteQuestion();
                        }
                      }
                    }}
                    disabled={deletingQuestionId === editQuestionId}
                  >
                    {deletingQuestionId === editQuestionId ? 'Deleting...' : 'Delete'}
                  </button>
                  <div className="flex space-x-3">
                    <button
                      type="button"
                      className="px-4 py-2 bg-gray-200 rounded-md text-gray-700 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={() => setShowEditModal(false)}
                      disabled={questionOperationLoading || deletingQuestionId === editQuestionId}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-primary-600 rounded-md text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={handleUpdateQuestion}
                      disabled={questionOperationLoading}
                    >
                      {questionOperationLoading ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </div>
              </form>
              <button
                className="absolute top-2 right-2 text-gray-400 hover:text-gray-600"
                onClick={() => setShowEditModal(false)}
                aria-label="Close"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-6 h-6">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default EditTemplate;